import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { GamePlayer } from '~/components/game/GamePlayer';
import { EpisodeList } from '~/components/landing/EpisodeList';
import { LandingView } from '~/components/landing/LandingView';
import { SeasonSelector } from '~/components/landing/SeasonSelector';
import { AppHeader } from '~/components/layout/AppHeader';
import { OfferPage } from '~/components/offer/OfferPage';
import { BrandedTransitionScreen } from '~/components/ui/BrandedTransitionScreen';
import { ErrorState } from '~/components/ui/ErrorState';
import { YakSplashScreen } from '~/components/ui/YakSplashScreen';
import { HeaderProvider, useHeader } from '~/contexts/HeaderContext';
import { useAnalytics } from '~/hooks/useAnalytics';
import { apiClient, getApiBaseUrl } from '~/lib/api';
import { resolveDomain } from '~/lib/domain';
import { readFinalScore } from '~/lib/finalScore';
import {
  fetchResolvedOffer,
  fireOfferFallbackBeacon,
  type ResolvedOfferResponse,
} from '~/lib/offerFetch';
import type { PublicEpisodeInfo, PublicLandingPage } from '~/types';
import { PublicLandingPageSchema } from '~/types/schemas';

// =============================================================================
// Path Parsing — extract composite slug and sub-route from URL
// =============================================================================

const KNOWN_SUBROUTES = new Set(['episodes', 'score', 'offer']);

interface ParsedPath {
  slug: string;
  subRoute: string;
  episodeId?: string;
}

function parsePath(pathname: string): ParsedPath {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return { slug: '', subRoute: 'index' };

  const last = segments[segments.length - 1];
  const secondToLast = segments.length >= 2 ? segments[segments.length - 2] : '';

  // /slug.../game/:episodeId
  if (secondToLast === 'game' && segments.length >= 3) {
    return {
      slug: segments.slice(0, -2).join('/'),
      subRoute: 'game',
      episodeId: last,
    };
  }

  // /slug.../offer/:episodeId
  if (secondToLast === 'offer' && segments.length >= 3) {
    return {
      slug: segments.slice(0, -2).join('/'),
      subRoute: 'offer',
      episodeId: last,
    };
  }

  // /slug.../episodes | /slug.../score | /slug.../offer (legacy, no episodeId)
  if (KNOWN_SUBROUTES.has(last) && segments.length > 1) {
    return {
      slug: segments.slice(0, -1).join('/'),
      subRoute: last,
    };
  }

  // Entire path is the slug (index route)
  return { slug: segments.join('/'), subRoute: 'index' };
}

// =============================================================================
// Track which slugs have already shown their branded transition
// =============================================================================

const transitionedSlugs = new Set<string>();

// =============================================================================
// Route Definition
// =============================================================================

export const Route = createFileRoute('/$')({
  beforeLoad: async ({ location, context }) => {
    // SSR guard: window is not available on the server
    // In SPA mode, skip data fetching during SSR - client will handle it
    if (typeof window === 'undefined') {
      // Return minimal context for SSR, client will fetch data
      const { slug, subRoute, episodeId } = parsePath(location.pathname);
      return { slug, subRoute, episodeId };
    }

    const { type, value } = resolveDomain(
      window.location.hostname,
      window.location.pathname,
    );

    // ── Custom domain handling (existing behavior) ──
    if (type === 'custom_domain') {
      const slug = value;
      const path = location.pathname.replace(/^\//, '');

      if (path === 'episodes') {
        throw redirect({ to: '/$slug/episodes', params: { slug } });
      }
      if (path.startsWith('game/')) {
        const episodeId = path.slice(5);
        if (episodeId) {
          throw redirect({
            to: '/$slug/game/$episodeId',
            params: { slug, episodeId },
          });
        }
      }
      if (path === 'score') {
        const searchParams = new URLSearchParams(location.searchStr);
        throw redirect({
          to: '/$slug/score',
          params: { slug },
          search: {
            score: Number(searchParams.get('score') || 0),
            totalQuestions: Number(searchParams.get('totalQuestions') || 1),
          },
        });
      }
      if (path === 'offer') {
        throw redirect({ to: '/$slug/offer', params: { slug } });
      }
      throw redirect({ to: '/$slug', params: { slug } });
    }

    // ── Primary host: composite slug paths ──
    const { slug, subRoute, episodeId } = parsePath(location.pathname);
    if (!slug) return {};

    const landingPage: PublicLandingPage =
      await context.queryClient.ensureQueryData({
        queryKey: ['landingPage', slug],
        queryFn: async () => {
          const data = await apiClient<unknown>(
            `/v1/public/landing-pages/by-slug?slug=${encodeURIComponent(slug)}`,
          );
          return PublicLandingPageSchema.parse(data) as PublicLandingPage;
        },
        staleTime: 1000 * 60 * 10,
      });

    return { slug, landingPage, subRoute, episodeId };
  },
  component: SplatLayout,
  pendingComponent: () => <YakSplashScreen />,
  errorComponent: ({ error, reset }) => (
    <ErrorState
      message={error?.message || 'Failed to load this page. Please try again.'}
      onRetry={reset}
    />
  ),
});

// =============================================================================
// Layout — splash → transition → content with internal sub-routing
// =============================================================================

interface SplatViewProps {
  slug: string;
  landingPage: PublicLandingPage;
  onNavigate: (view: string, episodeId?: string, sessionRef?: string) => void;
}

function SplatLayout() {
  // Track client mount to avoid hydration mismatch
  // SSR renders YakSplashScreen, client hydrates with same, then switches to actual content
  const [isMounted, setIsMounted] = useState(false);

  const context = Route.useRouteContext() as {
    slug?: string;
    landingPage?: PublicLandingPage;
    subRoute?: string;
    episodeId?: string;
  };

  const { slug, landingPage: contextLandingPage } = context;

  // Fetch landing page data client-side if not available from route context (SSR case)
  const { data: fetchedLandingPage, isLoading, error } = useQuery({
    queryKey: ['landingPage', slug],
    queryFn: async () => {
      const data = await apiClient<unknown>(
        `/v1/public/landing-pages/by-slug?slug=${encodeURIComponent(slug!)}`,
      );
      return PublicLandingPageSchema.parse(data) as PublicLandingPage;
    },
    enabled: isMounted && !!slug && !contextLandingPage,
    staleTime: 1000 * 60 * 10,
  });

  const landingPage = contextLandingPage || fetchedLandingPage;

  // Sub-route state (allows internal navigation without full page reload)
  const [view, setView] = useState(context.subRoute ?? 'index');
  const [episodeId, setEpisodeId] = useState(context.episodeId);
  const [offerSessionRef, setOfferSessionRef] = useState<string | undefined>(undefined);

  // Transition only on first visit per slug
  // Initialize to false for SSR consistency, then check on client mount
  const [showTransition, setShowTransition] = useState(false);

  // Mark as mounted after hydration completes
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Check transition state on client mount only (avoids hydration mismatch)
  useEffect(() => {
    if (isMounted && slug && !transitionedSlugs.has(slug)) {
      setShowTransition(true);
    }
  }, [isMounted, slug]);

  const handleTransitionComplete = useCallback(() => {
    if (slug) transitionedSlugs.add(slug);
    setShowTransition(false);
  }, [slug]);

  // Safety timeout for transition
  useEffect(() => {
    if (!showTransition) return;
    const timer = setTimeout(() => {
      if (slug) transitionedSlugs.add(slug);
      setShowTransition(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [showTransition, slug]);

  // Navigate between views (updates URL + state)
  // For the offer view, newEpisodeId is the episode being claimed; sessionRef
  // is appended as a search param so the offer fetch can resolve per-episode overrides.
  const navigateTo = useCallback(
    (newView: string, newEpisodeId?: string, newSessionRef?: string) => {
      let path: string;
      if (newView === 'index') {
        path = `/${slug}`;
      } else if (newView === 'game' && newEpisodeId) {
        path = `/${slug}/game/${newEpisodeId}`;
      } else if (newView === 'offer' && newEpisodeId) {
        path = `/${slug}/offer/${newEpisodeId}`;
        if (newSessionRef) {
          path += `?sessionRef=${encodeURIComponent(newSessionRef)}`;
        }
      } else {
        path = `/${slug}/${newView}`;
      }
      window.history.pushState({}, '', path);
      setView(newView);
      if (newEpisodeId) setEpisodeId(newEpisodeId);
      if (newView === 'offer') setOfferSessionRef(newSessionRef);
    },
    [slug],
  );

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const parsed = parsePath(window.location.pathname);
      setView(parsed.subRoute);
      setEpisodeId(parsed.episodeId);
      if (parsed.subRoute === 'offer') {
        const sp = new URLSearchParams(window.location.search);
        setOfferSessionRef(sp.get('sessionRef') ?? undefined);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // ── Guards ──

  // Always render YakSplashScreen during SSR and initial client render
  // This prevents hydration mismatch since SSR prerenders "/" but client may load "/slug/..."
  if (!isMounted) {
    return <YakSplashScreen />;
  }

  if (!slug) {
    return <ErrorState message="Landing page not found" />;
  }

  if (isLoading) {
    return <YakSplashScreen />;
  }

  if (error || !landingPage) {
    return <ErrorState message={error?.message || "Landing page data unavailable"} />;
  }

  // ── Transition screen ──

  if (showTransition) {
    return (
      <BrandedTransitionScreen
        branding={landingPage.branding}
        duration={1000}
        onTransitionComplete={handleTransitionComplete}
      />
    );
  }

  // ── Sub-route rendering ──

  const renderView = () => {
    switch (view) {
      case 'episodes':
        return (
          <SplatEpisodesView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
          />
        );
      case 'game':
        return (
          <SplatGameView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
            episodeId={episodeId!}
          />
        );
      case 'score':
        return (
          <SplatScoreView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
          />
        );
      case 'offer':
        return (
          <SplatOfferView
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
            episodeId={episodeId}
            sessionRef={offerSessionRef}
          />
        );
      case 'index':
      default:
        return (
          <SmartRedirect
            slug={slug}
            landingPage={landingPage}
            onNavigate={navigateTo}
          />
        );
    }
  };

  return (
    <HeaderProvider>
      <AppHeader logoUrl={landingPage.branding.logoUrl} />
      {renderView()}
    </HeaderProvider>
  );
}

// =============================================================================
// Landing Index — branded landing with a real Play CTA (no auto-redirect)
// =============================================================================

function SmartRedirect({ slug, landingPage, onNavigate }: SplatViewProps) {
  const { trackEvent } = useAnalytics({ slug });

  useEffect(() => {
    trackEvent('view');
  }, [trackEvent]);

  const handlePlay = useCallback(() => {
    if (landingPage.targetType === 'show') {
      onNavigate('episodes');
    } else {
      onNavigate('game', landingPage.targetId);
    }
  }, [landingPage, onNavigate]);

  return (
    <LandingView landingPage={landingPage}>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handlePlay}
          className="px-8 py-3 md:px-10 md:py-4 rounded-full text-lg md:text-xl font-bold text-white shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2"
          style={{
            backgroundColor: landingPage.branding.primaryColor,
          }}
        >
          Play
        </button>
      </div>
    </LandingView>
  );
}

// =============================================================================
// Episodes View
// =============================================================================

function SplatEpisodesView({ slug, landingPage, onNavigate }: SplatViewProps) {
  const { trackEvent } = useAnalytics({ slug });

  useEffect(() => {
    trackEvent('view');
  }, [trackEvent]);

  const seasonMap = useMemo(() => {
    if (!landingPage.episodes) return new Map<string, PublicEpisodeInfo[]>();
    const map = new Map<string, PublicEpisodeInfo[]>();
    for (const ep of landingPage.episodes) {
      const season = ep.seasonName ?? 'Season 1';
      const list = map.get(season) ?? [];
      list.push(ep);
      map.set(season, list);
    }
    return map;
  }, [landingPage.episodes]);

  const seasonNames = useMemo(() => Array.from(seasonMap.keys()), [seasonMap]);
  const [activeSeason, setActiveSeason] = useState('');

  useEffect(() => {
    if (seasonNames.length > 0 && !activeSeason) {
      setActiveSeason(seasonNames[0]);
    }
  }, [seasonNames, activeSeason]);

  const activeEpisodes = useMemo(
    () => seasonMap.get(activeSeason) ?? [],
    [seasonMap, activeSeason],
  );

  return (
    <LandingView landingPage={landingPage}>
      <div className="flex flex-col gap-4">
        <SeasonSelector
          seasons={seasonNames}
          activeSeason={activeSeason}
          onSelectSeason={setActiveSeason}
          branding={landingPage.branding}
        />
        <EpisodeList
          episodes={activeEpisodes}
          onSelectEpisode={(id) => onNavigate('game', id)}
          branding={landingPage.branding}
        />
      </div>
    </LandingView>
  );
}

// =============================================================================
// Game View
// =============================================================================

function SplatGameView({
  slug,
  landingPage,
  episodeId,
  onNavigate,
}: SplatViewProps & {
  episodeId: string;
}) {
  const { setHideHeader } = useHeader();

  // Hide header immediately on game view and restore when leaving
  useEffect(() => {
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [setHideHeader]);

  const handleGameComplete = useCallback(
    (data?: { score: number; totalQuestions: number }) => {
      // Re-show the header before navigating away. The score view reads the
      // server-finalized totals from sessionStorage (written by GamePlayer),
      // so no scoreData is threaded through React state here.
      setHideHeader(false);
      if (data) {
        onNavigate('score');
      } else {
        // Pass episodeId and sessionRef so the offer view can resolve per-episode overrides.
        const final = readFinalScore(slug);
        onNavigate('offer', episodeId, final?.sessionRef);
      }
    },
    [onNavigate, setHideHeader, slug, episodeId],
  );

  const handlePlayStart = useCallback(() => {
    setHideHeader(true);
  }, [setHideHeader]);

  return (
    <div className="h-screen w-screen overflow-hidden">
      <GamePlayer
        slug={slug}
        episodeId={episodeId}
        gameOptions={landingPage.gameOptions}
        onGameComplete={handleGameComplete}
        onPlayStart={handlePlayStart}
        className="h-full w-full"
      />
    </div>
  );
}

// =============================================================================
// Score View
// =============================================================================

function SplatScoreView({
  slug,
  landingPage,
  onNavigate,
}: SplatViewProps) {
  // The canonical post-game score lives in sessionStorage (written by
  // GamePlayer at finalize time). Reading it here means refresh, deep-link,
  // and claim recovery all show the same numbers as the standard score route.
  const finalScore = useMemo(() => readFinalScore(slug), [slug]);

  const correctAnswers = finalScore?.correctAnswers ?? finalScore?.score ?? 0;
  const totalQuestions = finalScore?.totalQuestions ?? 0;
  const percentage =
    totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
  const hadFinalizeError = finalScore?.hadFinalizeError === true;

  useEffect(() => {
    const timer = setTimeout(() => {
      const final = readFinalScore(slug);
      onNavigate('offer', final?.episodeId, final?.sessionRef);
    }, 2000);
    return () => clearTimeout(timer);
  }, [onNavigate, slug]);

  return (
    <div
      className="flex h-screen w-screen items-center justify-center p-2 sm:p-4 md:p-8 lg:p-16"
      style={{
        backgroundImage:
          'url(https://s3.amazonaws.com/yak.assets/52xP82c.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="flex h-full w-full max-w-[95vw] flex-col items-center justify-center overflow-hidden rounded-xl bg-white/95 p-4 shadow-2xl md:aspect-video md:h-auto md:max-w-7xl md:rounded-2xl md:p-12">
        {hadFinalizeError && (
          <div
            role="status"
            data-testid="finalize-error-banner"
            className="mb-3 md:mb-6 px-3 py-1.5 md:px-4 md:py-2 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-xs md:text-sm font-medium"
          >
            Score not confirmed — please try again next time.
          </div>
        )}
        <div className="flex flex-col items-center gap-4 text-center md:gap-8">
          {landingPage.branding.logoUrl && (
            <img
              src={landingPage.branding.logoUrl}
              alt="Brand logo"
              className="h-12 object-contain md:h-20"
            />
          )}
          <div className="flex flex-col gap-2 md:gap-4">
            <h1
              className="text-4xl font-bold md:text-6xl"
              style={{ color: landingPage.branding.primaryColor }}
            >
              {correctAnswers}/{totalQuestions}
            </h1>
            <p className="text-sm font-medium text-gray-500 md:text-lg">
              correct answers
            </p>
            <p
              className="text-2xl font-semibold md:text-4xl"
              style={{ color: landingPage.branding.secondaryColor }}
            >
              {percentage}%
            </p>
          </div>
          <p className="text-lg font-medium text-gray-700 md:text-2xl">
            Thanks for playing!
          </p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Offer View
// =============================================================================
//
// The splat-routed offer view mirrors the dedicated `/$slug/offer/$episodeId`
// route. It implements the same third-loop contracts:
//   - R1-H1  X-Session-Ref header + Referrer-Policy via document meta (set
//            once on the splat route below).
//   - R1-H2  resolvedFrom='override_disabled' → terminal score-screen state.
//   - R1-M2  no-sessionRef CTA → plain <a>.
//   - R5-H1  features.episodeOffersEnabled gate.
//   - R5-M7  bounded outage fallback hierarchy + jittered/sampled beacon.

type OfferRenderMode = 'normal' | 'override_disabled';

function SplatOfferView({
  slug,
  landingPage,
  onNavigate,
  episodeId,
  sessionRef,
}: SplatViewProps & { episodeId?: string; sessionRef?: string }) {
  const { trackEvent } = useAnalytics({ slug });
  const queryClient = useQueryClient();

  // R1-H1: scope `<meta name="referrer" content="no-referrer">` to the offer
  // view. The splat route handles many sub-routes; we only want the policy
  // active while the offer page is mounted so inline override HTML can't
  // leak the offer URL via the Referer header.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    meta.setAttribute('data-offer-referrer-policy', 'true');
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const storedSessionRef = useMemo(() => {
    const final = readFinalScore(slug);
    return final?.sessionRef ?? sessionRef ?? null;
  }, [slug, sessionRef]);

  const [resolutionToken, setResolutionToken] = useState<string | null>(null);
  const [renderMode, setRenderMode] = useState<OfferRenderMode>('normal');

  const episodeOffersEnabled =
    landingPage.features?.episodeOffersEnabled === true;

  const { data: offerData, isLoading: offerLoading } = useQuery<
    ResolvedOfferResponse | null
  >({
    queryKey: ['offer', slug, episodeId ?? null, storedSessionRef],
    enabled: episodeOffersEnabled,
    staleTime: 0,
    queryFn: async () => {
      const result = await fetchResolvedOffer(
        slug,
        episodeId ?? null,
        storedSessionRef,
      );

      if ('data' in result) {
        setResolutionToken(result.data.resolutionToken);
        if (result.data.resolvedFrom === 'override_disabled') {
          setRenderMode('override_disabled');
        }
        return result.data;
      }

      fireOfferFallbackBeacon(slug, result.error);

      const cached = queryClient.getQueryData<PublicLandingPage>([
        'landingPage',
        slug,
      ]);
      if (cached) return null;

      const refetchController = new AbortController();
      const refetchTimer = setTimeout(() => refetchController.abort(), 5000);
      try {
        await queryClient.fetchQuery({
          queryKey: ['landingPage', slug],
          queryFn: async () => {
            const data = await apiClient<unknown>(
              `/v1/public/landing-pages/by-slug?slug=${encodeURIComponent(slug)}`,
              { signal: refetchController.signal },
            );
            return PublicLandingPageSchema.parse(data) as PublicLandingPage;
          },
        });
        clearTimeout(refetchTimer);
        return null;
      } catch {
        clearTimeout(refetchTimer);
        setRenderMode('override_disabled');
        fireOfferFallbackBeacon(slug, 'hard_fallback');
        return null;
      }
    },
  });

  const resolvedLandingPage = queryClient.getQueryData<PublicLandingPage>([
    'landingPage',
    slug,
  ]);
  const fallbackSource = resolvedLandingPage ?? landingPage;

  const offerContent = offerData
    ? offerData.offerContent
    : fallbackSource.offerContent;
  const forwardUrl = offerData
    ? offerData.forwardUrl
    : fallbackSource.forwardUrl;
  const showYakOffer = offerData
    ? offerData.showYakOffer
    : fallbackSource.showYakOffer;

  const useDirectClaimAnchor = resolutionToken === null;

  const handlePlayAgain = useCallback(() => {
    if (landingPage.targetType === 'show') {
      onNavigate('episodes');
    } else {
      onNavigate('game', landingPage.targetId);
    }
  }, [landingPage, onNavigate]);

  const handleClaimClick = useCallback(() => {
    if (resolutionToken === null) return;

    const final = readFinalScore(slug);
    const ref = final?.sessionRef ?? sessionRef;
    if (!ref) {
      if (forwardUrl) {
        window.open(forwardUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    const base = getApiBaseUrl();
    const url =
      `${base}/v1/public/claim/${encodeURIComponent(ref)}` +
      `?slug=${encodeURIComponent(slug)}` +
      `&token=${encodeURIComponent(resolutionToken)}`;
    window.location.href = url;
  }, [slug, forwardUrl, sessionRef, resolutionToken]);

  if (renderMode === 'override_disabled') {
    return <SplatOfferTerminalScore landingPage={landingPage} slug={slug} />;
  }

  if (offerLoading) {
    return (
      <OfferPage
        offerContent={landingPage.offerContent}
        branding={landingPage.branding}
        forwardUrl={landingPage.forwardUrl}
        title={landingPage.title}
        onClaimClick={handleClaimClick}
        onForwardClick={() => trackEvent('offer_click')}
        onOfferView={() => trackEvent('offer_view')}
        targetType={landingPage.targetType}
        onPlayAgain={handlePlayAgain}
        showYakOffer={landingPage.showYakOffer}
        useDirectClaimAnchor={useDirectClaimAnchor}
      />
    );
  }

  return (
    <OfferPage
      offerContent={offerContent}
      branding={landingPage.branding}
      forwardUrl={forwardUrl}
      title={landingPage.title}
      onClaimClick={handleClaimClick}
      onForwardClick={() => trackEvent('offer_click')}
      onOfferView={() => trackEvent('offer_view')}
      targetType={landingPage.targetType}
      onPlayAgain={handlePlayAgain}
      showYakOffer={showYakOffer}
      useDirectClaimAnchor={useDirectClaimAnchor}
    />
  );
}

/**
 * Terminal score-screen render used by the splat offer view when overrides
 * are disabled (R1-H2) or the outage cascade exhausted (R5-M7).
 */
function SplatOfferTerminalScore({
  landingPage,
  slug,
}: {
  landingPage: PublicLandingPage;
  slug: string;
}) {
  const finalScore = useMemo(() => readFinalScore(slug), [slug]);
  const correctAnswers = finalScore?.correctAnswers ?? finalScore?.score ?? 0;
  const totalQuestions = finalScore?.totalQuestions ?? 0;
  const percentage =
    totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

  return (
    <div
      className="flex h-screen w-screen items-center justify-center p-2 sm:p-4 md:p-8 lg:p-16"
      style={{
        backgroundImage:
          'url(https://s3.amazonaws.com/yak.assets/52xP82c.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="offer-terminal-score-screen"
    >
      <div className="flex h-full w-full max-w-[95vw] flex-col items-center justify-center overflow-hidden rounded-xl bg-white/95 p-4 shadow-2xl md:aspect-video md:h-auto md:max-w-7xl md:rounded-2xl md:p-12">
        <div className="flex flex-col items-center gap-4 text-center md:gap-8">
          {landingPage.branding.logoUrl && (
            <img
              src={landingPage.branding.logoUrl}
              alt="Brand logo"
              className="h-12 object-contain md:h-20"
            />
          )}
          <div className="flex flex-col gap-2 md:gap-4">
            <h1
              className="text-4xl font-bold md:text-6xl"
              style={{ color: landingPage.branding.primaryColor }}
            >
              {correctAnswers}/{totalQuestions}
            </h1>
            <p className="text-sm font-medium text-gray-500 md:text-lg">
              correct answers
            </p>
            <p
              className="text-2xl font-semibold md:text-4xl"
              style={{ color: landingPage.branding.secondaryColor }}
            >
              {percentage}%
            </p>
          </div>
          <p className="text-lg font-medium text-gray-700 md:text-2xl">
            Thanks for playing!
          </p>
        </div>
      </div>
    </div>
  );
}
